import SERVICES_API_V1 from 'api/services/v1'
import clsx from 'clsx'
import renderCheckbox from 'components/FormControls/checkboxField'
import renderSelect from 'components/FormControls/select'
import renderInputField from 'components/FormControls/textInput'
import GridSpinner from 'components/UI/GridSpinner'
import { Icon, Tooltip, Typography } from 'gu-ui-kit'
import omit from 'lodash/omit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, Form, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import domainValidator from 'utils/validators/domainValidator'
import * as Yup from 'yup'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/SubmitButtons'
import { IS_APP_PRODUCT_ADSIMPLE } from 'utils/constants/product'
import { isRequiredField } from 'utils/isRequiredField'

export const TiktokSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    network: Yup.string().required('common::validation_messages.required'),
    business_center_id: Yup.string()
      .matches(
        /^\d+$/,
        'services::forms.register_account.tiktok.fields.business_center_id.validation_messages.incorrect_format',
      )
      .required('common::validation_messages.required'),
    industry: Yup.string().required('common::validation_messages.required'),
    site: Yup.string()
      .test('domainValidator', 'common::validation_messages.incorrect_url', domainValidator)
      .required('common::validation_messages.required'),
    country: Yup.string().required('common::validation_messages.required'),
    apply_agreement: Yup.boolean().oneOf([true], 'common::validation_messages.required'),
  })

let CreateTiktokAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  formHeader,
  networkId,
  form,
  array,
  multiLogins,
  networkType,
}) => {
  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [countries, setCountries] = useState([])
  const [industriesLoading, setIndustriesLoading] = useState(true)
  const [industriesList, setIndustriesList] = useState([])
  const pathGen = useGeneratePathI18n()

  const { apply_agreement, i18n } = useSelector((state) => ({
    apply_agreement: selector(state, 'apply_agreement'),
    i18n: state['app'].i18n,
  }))

  useEffect(() => {
    if (!networkId) {
      return
    }

    if (industriesList.length === 0) {
      SERVICES_API_V1.getNetworkSettings({ networkId })
        .then(({ response: { data } }) => {
          setCountries(
            (data?.countries || []).map(({ id, name }) => ({
              value: id,
              label: name,
            })),
          )

          setIndustriesList(
            Object.keys(data?.industries || {}).map((key) => ({
              label: t(`industries::${key}`),
              items: data.industries[key].map((key) => ({
                value: key,
                label: t(`industries::${key}`),
              })),
            })),
          )
        })
        .finally(() => {
          setSettingsLoading(false)
          setIndustriesLoading(false)
        })
    } else {
      setIndustriesLoading(false)
    }
  }, [networkId])

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['apply_agreement'])),
    [submitAccountData],
  )

  return (
    <>
      {formHeader}

      <Form
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.tiktok.fields.login.label'}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="business_center_id"
            required={isRequiredField(TiktokSchema(networkType), 'business_center_id')}
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.tiktok.fields.business_center_id.label')}
            normalize={(value) => value.replace(/\D/g, '').substr(0, 50) ?? ''}
            t={t}
          />
          {IS_APP_PRODUCT_ADSIMPLE && (
            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.register_account.tiktok.fields.business_center_id.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'Label-Secondary'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          )}
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t('services::forms.register_account.tiktok.fields.industry.label')}
            name="industry"
            required={isRequiredField(TiktokSchema(networkType), 'industry')}
            component={renderSelect}
            fullWidth
            t={t}
            normalize={(value) => value ?? ''}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
            options={industriesList}
            readOnly={industriesLoading}
            ControlProps={{
              startAdornment: industriesLoading && <GridSpinner width={24} />,
            }}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="site"
            required={isRequiredField(TiktokSchema(networkType), 'site')}
            type="url"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.tiktok.fields.site.label')}
            t={t}
            normalize={(value) => value.trim()}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t('services::forms.register_account.tiktok.fields.country.label')}
            name="country"
            required={isRequiredField(TiktokSchema(networkType), 'country')}
            component={renderSelect}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
            readOnly={settingsLoading}
            fullWidth
            t={t}
            normalize={(value = '') => value}
            options={countries}
            mode={'filter'}
            noResultsLabel={t('common::empty_results.header')}
            ControlProps={{
              startAdornment: settingsLoading && <GridSpinner width={24} />,
            }}
          />
        </div>

        <Typography variant={'Body-25'}>
          <Field
            name="apply_agreement"
            required={isRequiredField(TiktokSchema(networkType), 'apply_agreement')}
            checked={apply_agreement}
            component={renderCheckbox}
            label={
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'services::forms.register_account.tiktok.fields.apply_agreement.label',
                    {
                      langsection: i18n === 'ru' ? '' : pathGen(ROUTES.MAIN),
                    },
                  ),
                }}
              />
            }
            t={t}
          />
        </Typography>

        <SubmitButtons
          formName={form}
          submitted={submitAccountData.submitted}
          submitting={submitting}
          onClose={onClose}
        />
      </Form>
    </>
  )
}

CreateTiktokAccountForm = reduxForm({
  form: 'CreateTiktokAccountForm',
  initialValues: {
    login: '',
    network: '',
    business_center_id: '',
    industry: '',
    site: '',
    country: '',
    apply_agreement: false,
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateTiktokAccountForm)

CreateTiktokAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  formHeader: PropTypes.node,
  networkType: PropTypes.string,
}

export default CreateTiktokAccountForm
